import type { ReportBlock, ReportDataset } from "@/profitability/types";

/**
 * Places the saved blocks on a twelve column dashboard. Scripts declare what a
 * report holds, never how it looks, so the arrangement is decided here.
 * Figures take a row of equal tiles. A chart asks for the width its own data
 * needs, which is what gives the grid its bento shape: a pie or a handful of
 * bars stays small, a chart of every job type takes the page. Charts then fill
 * each row exactly, so the grid never shows a hole, and tables close the page.
 */
const COLUMN_SPAN = {
  3: "sm:col-span-2 lg:col-span-3",
  4: "sm:col-span-2 lg:col-span-4",
  6: "sm:col-span-2 lg:col-span-6",
  8: "sm:col-span-2 lg:col-span-8",
  12: "sm:col-span-2 lg:col-span-12",
} as const;

const FIGURE_SPAN = {
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  6: "lg:col-span-6",
  12: "sm:col-span-2 lg:col-span-12",
} as const;

const MAX_FIGURES_IN_ROW = 4;

const CHART_HEIGHT = {
  wide: "h-72",
  rest: "h-64",
} as const;

const CHART_TYPES = ["bar", "line", "pie"] as const;
const ROW = 12;
const MAX_IN_ROW = 3;
const SMALL_SERIES = 6;
const WIDE_SERIES = 14;
/** Counting stops here: anything beyond is wide whatever the exact total. */
const COUNT_LIMIT = 40;

export const isChartBlock = (block: ReportBlock) =>
  CHART_TYPES.some((type) => type === block.type);

export type BentoPlacement = {
  span: string;
  /** Fixed height class, so each chart row reads as one band. */
  height?: string;
  /** A card too narrow for a chart legend beside the plot. */
  narrow: boolean;
};

const seriesCount = (block: ReportBlock, datasets: ReportDataset[]) => {
  if (
    block.type === "pie" ||
    block.type === "summary" ||
    block.type === "table"
  )
    return 0;
  const rows = datasets.find((item) => item.id === block.dataset)?.rows ?? [];
  const seen = new Set<string>();
  for (const row of rows) {
    seen.add(String(row[block.x]));
    if (seen.size >= COUNT_LIMIT) break;
  }
  return seen.size;
};

const preferredSpan = (block: ReportBlock, datasets: ReportDataset[]) => {
  if (block.type === "pie") return 4;
  const series = seriesCount(block, datasets);
  if (series <= SMALL_SERIES) return 4;
  return series <= WIDE_SERIES ? 6 : 12;
};

const chartRows = (charts: ReportBlock[], datasets: ReportDataset[]) => {
  const rows: ReportBlock[][] = [];
  let row: ReportBlock[] = [];
  let used = 0;
  charts.forEach((block) => {
    const span = preferredSpan(block, datasets);
    if (row.length && used + span > ROW) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(block);
    used += span;
  });
  if (row.length) rows.push(row);
  // A pie alone in a row would be stretched across the page, so it joins a
  // neighbouring row with room and that row gives up some width.
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const [pie] = rows[index];
    if (rows[index].length !== 1 || pie.type !== "pie") continue;
    const before = (rows[index - 1]?.length ?? MAX_IN_ROW) < MAX_IN_ROW;
    const after = (rows[index + 1]?.length ?? MAX_IN_ROW) < MAX_IN_ROW;
    if (!before && !after) continue;
    if (before) rows[index - 1] = [...rows[index - 1], pie];
    else rows[index + 1] = [pie, ...rows[index + 1]];
    rows.splice(index, 1);
  }
  return rows;
};

/** Every row adds up to twelve. */
const chartSpans = (charts: ReportBlock[], datasets: ReportDataset[]) => {
  const spans = new Map<string, number>();
  chartRows(charts, datasets).forEach((row) => {
    const preferred = row.map((block) => preferredSpan(block, datasets));
    const total = preferred.reduce((sum, span) => sum + span, 0);
    const even = Math.floor(ROW / row.length);
    // Pies share a row equally; anything else keeps the width its data asked
    // for, unless the row is over full and has to be split evenly.
    const pies = row.every((block) => block.type === "pie");
    row.forEach((block, index) =>
      spans.set(block.id, total > ROW || pies ? even : preferred[index]),
    );
    // Whatever is left over goes to one card, so a row is never part empty.
    // A pie is the last to take it: slices gain nothing from a wide band.
    const grow =
      [...row].reverse().find((block) => block.type !== "pie") ?? row[0];
    const assigned = row.reduce((sum, block) => sum + spans.get(block.id)!, 0);
    spans.set(grow.id, spans.get(grow.id)! + ROW - assigned);
  });
  return spans;
};

const TYPE_ORDER = { summary: 0, bar: 1, line: 1, pie: 1, table: 2 } as const;

export const bentoOrder = (blocks: ReportBlock[]) =>
  [...blocks].sort((a, b) => TYPE_ORDER[a.type] - TYPE_ORDER[b.type]);

/**
 * Figures fill whole rows, so they never sit beside a chart and get stretched
 * to its height. The rows are as even as the count allows: six figures are two
 * rows of three, not a row of four and a row of two.
 */
const summarySpans = (summaries: ReportBlock[]) => {
  const spans = new Map<string, number>();
  if (!summaries.length) return spans;
  const rows = Math.ceil(summaries.length / MAX_FIGURES_IN_ROW);
  const perRow = Math.ceil(summaries.length / rows);
  for (let start = 0; start < summaries.length; start += perRow) {
    const row = summaries.slice(start, start + perRow);
    row.forEach((block) => spans.set(block.id, ROW / row.length));
  }
  return spans;
};

export const isHeadlineFigure = (block: ReportBlock) =>
  block.type === "summary" && block.emphasis !== "supporting";

export const isSupportingFigure = (block: ReportBlock) =>
  block.type === "summary" && block.emphasis === "supporting";

export const bentoLayout = (
  blocks: ReportBlock[],
  datasets: ReportDataset[],
) => {
  const figures = summarySpans(blocks.filter(isHeadlineFigure));
  const spans = chartSpans(blocks.filter(isChartBlock), datasets);
  return new Map<string, BentoPlacement>(
    blocks.map((block) => {
      if (block.type === "summary")
        return [
          block.id,
          {
            span:
              FIGURE_SPAN[figures.get(block.id) as keyof typeof FIGURE_SPAN] ??
              FIGURE_SPAN[3],
            narrow: false,
          },
        ];
      const span = spans.get(block.id);
      if (!span) return [block.id, { span: COLUMN_SPAN[ROW], narrow: false }];
      return [
        block.id,
        {
          span:
            COLUMN_SPAN[span as keyof typeof COLUMN_SPAN] ?? COLUMN_SPAN[ROW],
          height: span >= 8 ? CHART_HEIGHT.wide : CHART_HEIGHT.rest,
          narrow: span <= 4,
        },
      ];
    }),
  );
};
